import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { ChannelAudience } from '@troia-v3/types';

import { countryLabel, formatCompact } from './formatMetrics';

const COLORS = ['#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'];

const toRows = (rec?: Record<string, number>): Array<{ k: string; v: number }> =>
  Object.entries(rec ?? {})
    .map(([k, v]) => ({ k, v }))
    .sort((a, b) => b.v - a.v)
    .slice(0, 6);

export const AudiencePanel: React.FC<{ audience: ChannelAudience | undefined }> = ({ audience }) => {
  const countries = toRows(audience?.byCountry);
  const ages = toRows(audience?.byAge);
  const genders = toRows(audience?.byGender);

  if (!countries.length && !ages.length && !genders.length) {
    return (
      <div className="cdash-empty" data-testid="cdash-audience-empty">
        <div className="cdash-empty-t">Sem dados de audiência ainda</div>
        <div className="cdash-empty-d">Os dados aparecem após o primeiro sync com insights.</div>
      </div>
    );
  }

  const maxAge = Math.max(1, ...ages.map((r) => r.v));
  const maxGender = Math.max(1, ...genders.map((r) => r.v));

  return (
    <div data-testid="cdash-audience">
      <span className="cdash-mlabel">Audiência</span>
      <div className="cdash-aud-grid">
        {countries.length ? (
          <div>
            <div className="cdash-kpi-l" style={{ marginBottom: 8 }}>Por país</div>
            <div style={{ position: 'relative', width: '100%', maxWidth: 200 }}>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={countries} cx="50%" cy="50%" innerRadius={46} outerRadius={70} paddingAngle={3} dataKey="v" strokeWidth={0}>
                    {countries.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, _n, item) => {
                      const row = (item as { payload?: { k?: string } } | undefined)?.payload;
                      return [formatCompact(Number(v)), countryLabel(String(row?.k ?? ''))];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="cdash-bars" style={{ marginTop: 8 }}>
              {countries.slice(0, 4).map((r, i) => (
                <div className="cdash-bar-row" key={r.k} style={{ gridTemplateColumns: '14px 1fr 48px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length] }} />
                  <span>{countryLabel(r.k)}</span>
                  <span className="cdash-bar-val">{formatCompact(r.v)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {ages.length || genders.length ? (
          <div>
            {ages.length ? (
              <>
                <div className="cdash-kpi-l" style={{ marginBottom: 8 }}>Por idade</div>
                <div className="cdash-bars">
                  {ages.map((r) => (
                    <div className="cdash-bar-row" key={r.k}>
                      <span>{r.k}</span>
                      <span className="cdash-bar-track">
                        <span className="cdash-bar-fill" style={{ width: `${(r.v / maxAge) * 100}%` }} />
                      </span>
                      <span className="cdash-bar-val">{formatCompact(r.v)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
            {genders.length ? (
              <>
                <div className="cdash-kpi-l" style={{ margin: '16px 0 8px' }}>Por gênero</div>
                <div className="cdash-bars">
                  {genders.map((r) => (
                    <div className="cdash-bar-row" key={r.k}>
                      <span>{r.k}</span>
                      <span className="cdash-bar-track">
                        <span className="cdash-bar-fill" style={{ width: `${(r.v / maxGender) * 100}%` }} />
                      </span>
                      <span className="cdash-bar-val">{formatCompact(r.v)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
